import * as React from 'react'
import styled from 'styled-components'
import Modal from 'LuxKit/Modal/Modal'
import type { FlightsClassTypes } from 'constants/flight'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import CharterBundleCalendarActions from 'components/OfferPage/Filters/CharterFlight/CharterBundleCalendarActions'
import MaldivesDirectSearchFlightCalendar from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from './MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useMaldivesDirectCalendarPricing } from './hooks/useMaldivesDirectCalendarPricing'
import type { DateRange } from './hooks/types'
import { mediaQueryUp } from 'components/utils/breakpoint'

const MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID = 'maldives-direct-search-calendar-modal'

/**
 * The default Modal header wraps `title` in a `<Heading lineClamp={2}>` which
 * applies `display: -webkit-box` and `-webkit-line-clamp: 2`. Putting
 * interactive JSX (FilterChips, SwitchButton) inside that title caused Safari
 * to strictly enforce the clamp, cropping the chips and rendering the
 * line-clamp ellipsis. We keep `title` as a plain string and render the
 * actions via `headerExtension`, then re-arrange the modal header grid on
 * desktop+ so the extension area sits on the same row as the title.
 *
 * The modal header is targeted via `data-testid` (which `Modal` automatically
 * appends `-header` to and forwards to `ModalHeader`) so the override does not
 * depend on styled-components hashes propagating through `Modal` →
 * `ModalBase` → `react-modal`. The doubled attribute selector raises
 * specificity above LuxKit's `> .extension-area` rule.
 */
const StyledMaldivesDirectSearchCalendarModal = styled(Modal)`
  ${mediaQueryUp.desktop} {
    [data-testid="${MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID}-header"][data-testid] {
      grid-template-columns: min-content auto 1fr min-content;
      grid-template-areas:
        "prepended-action title header-actions appended-action"
        ". subtitle subtitle .";

      > .extension-area {
        grid-area: header-actions;
        margin-top: 0;
        justify-self: end;
        align-self: center;
      }
    }
  }
`

interface CalendarSelectionOption {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
}

type CharterBundleCalendarActionsProps = React.ComponentProps<typeof CharterBundleCalendarActions>

interface MaldivesDirectSearchCalendarModalProps {
  isOpen: boolean
  title: string | React.ReactElement
  onClose: () => void
  onDateSelect: (option: CalendarSelectionOption) => void
  nights: number
  rooms: Array<App.Occupants>
  luxPlusEnabled: boolean
  dateRangesByMonth: Record<string, Array<DateRange>>
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClassForSearch: FlightsClassTypes
  selectedFareClass?: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  selectedOptionId: string
  canStreamHotelOffers: boolean
  onDurationChange: (nights: number) => void
  durationOptions: CharterBundleCalendarActionsProps['durationOptions']
  onLuxPlusToggle: (enabled: boolean) => void
  onFareClassChange: (fareClass: FlightsClassTypes) => void
  availableFareClasses: Array<FlightsClassTypes>
}

export default function MaldivesDirectSearchCalendarModal(props: MaldivesDirectSearchCalendarModalProps) {
  const {
    isOpen,
    title,
    onClose,
    onDateSelect,
    nights,
    rooms,
    luxPlusEnabled,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    fareClassForSearch,
    selectedFareClass,
    priceMode,
    selectedOptionId,
    canStreamHotelOffers,
    onDurationChange,
    durationOptions,
    onLuxPlusToggle,
    onFareClassChange,
    availableFareClasses,
  } = props
  const isDesktop = useScreenSizeUp('desktop')
  const { calendarPricingByDate } = useMaldivesDirectCalendarPricing({
    rooms,
    fareClass: fareClassForSearch,
    selectedFareClass,
    nights,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    dateRangesByMonth,
    disabled: !canStreamHotelOffers || !isOpen,
  })

  if (!isOpen) {
    return null
  }

  if (isDesktop) {
    return (
      <StyledMaldivesDirectSearchCalendarModal
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        headerExtension={
          <CharterBundleCalendarActions
            nights={nights}
            onDurationChange={onDurationChange}
            durationOptions={durationOptions}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={onLuxPlusToggle}
            fareClass={selectedFareClass}
            onFareClassChange={onFareClassChange}
            availableFareClasses={availableFareClasses}
          />
        }
        size="XL"
        data-testid={MALDIVES_DIRECT_SEARCH_CALENDAR_MODAL_TEST_ID}
      >
        <MaldivesDirectSearchFlightCalendar
          onDateSelect={onDateSelect}
          nights={nights}
          occupancies={rooms}
          luxPlusEnabled={luxPlusEnabled}
          dateRangesByMonth={dateRangesByMonth}
          selectedMonth={selectedMonth}
          onMonthChange={onMonthChange}
          fareClass={fareClassForSearch}
          priceMode={priceMode}
          selectedOptionId={selectedOptionId}
          mode="search"
          itemBundleDataByDate={calendarPricingByDate}
        />
      </StyledMaldivesDirectSearchCalendarModal>
    )
  }

  return (
    <Modal
      height="full"
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      isPrimaryActionHidden
      flushListItems
    >
      <MaldivesDirectSearchFlightCalendarMobile
        onDateSelect={onDateSelect}
        nights={nights}
        occupancies={rooms}
        luxPlusEnabled={luxPlusEnabled}
        dateRangesByMonth={dateRangesByMonth}
        selectedMonth={selectedMonth}
        onMonthChange={onMonthChange}
        fareClass={fareClassForSearch}
        priceMode={priceMode}
        selectedOptionId={selectedOptionId}
        mode="search"
        itemBundleDataByDate={calendarPricingByDate}
        headerActions={
          <CharterBundleCalendarActions
            nights={nights}
            onDurationChange={onDurationChange}
            durationOptions={durationOptions}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={onLuxPlusToggle}
            fareClass={selectedFareClass}
            onFareClassChange={onFareClassChange}
            availableFareClasses={availableFareClasses}
          />
        }
      />
    </Modal>
  )
}
